import { useState, useEffect, useRef } from "react";
import axios from "axios";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import checkMark from "../../../assets/images/check-mark .png";
import cross from "../../../assets/images/cross .png";
import headeLogo from "../../../assets/images/SS.webp";
import { useDbContext } from "../../../context/DbContext";
import { useSelector } from "react-redux";
import { selectActiveFyid, selectActiveFy } from "../../../app/financialYearSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

// ── Static Data ─────────────────────────────────────────────────────────────
const MONTHS = [
  { label: "January", value: "January" },
  { label: "February", value: "February" },
  { label: "March", value: "March" },
  { label: "April", value: "April" },
  { label: "May", value: "May" },
  { label: "June", value: "June" },
  { label: "July", value: "July" },
  { label: "August", value: "August" },
  { label: "September", value: "September" },
  { label: "October", value: "October" },
  { label: "November", value: "November" },
  { label: "December", value: "December" }
];

const ORDER_BY_OPTIONS = [
  { label: "Entry No", value: "Entry No" },
  { label: "Date", value: "Date" },
  { label: "Supplier", value: "Supplier" },
  { label: "Bill No", value: "Bill No" }
];

const REPORT_TYPES = [
  { label: "Summary", value: "Summary" },
  { label: "ItemWise", value: "ItemWise" },
  { label: "Capital Summary", value: "Capital Summary" },
  { label: "Expense Summary", value: "Expense Summary" },
  { label: "ItemWise Comparison Stock Rate", value: "ItemWise Comparison Stock Rate" }
];

const TAX_TYPES = [
  { label: "T", value: "T" },
  { label: "NT", value: "NT" }
];

const PURCHASE_TYPES = [
  { label: "Purchase", value: "Purchase" },
  { label: "InterState", value: "InterState" },
  { label: "Composite", value: "Composite" },
  { label: "UnRegistered Dealer", value: "UnRegistered Dealer" },
  { label: "Branch Transfer", value: "Branch Transfer" },
  { label: "Imports", value: "Imports" }
];
// ────────────────────────────────────────────────────────────────────────────

function PurchaseListDialog({ onClose, openReport, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const fyid = useSelector(selectActiveFyid);
  const activeFy = useSelector(selectActiveFy);
  const activeLocation = useSelector(selectActiveLocation);

  // ── Fetched option states ──
  const [productOpts, setProductOpts] = useState([]);   // [{ slNo, itemCode, itemName }]
  const [locationOpts, setLocationOpts] = useState([]);
  const [categoryOpts, setCategoryOpts] = useState([]);
  const [subCategoryOpts, setSubCategoryOpts] = useState([]);
  const [mfrOpts, setMfrOpts] = useState([]);
  const [supplierOpts, setSupplierOpts] = useState([]);
  const [salesmanOpts, setSalesmanOpts] = useState([]);
  const [purchaseForms, setPurchaseForms] = useState([]);
  const [groupOpts, setGroupOpts] = useState([]);
  const [rateClassOpts, setRateClassOpts] = useState([]);
  const [modelOpts, setModelOpts] = useState([]);

  // ── Fetch products (ItemCode / ItemName) ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/main/selectallProduct/${dbCode}`)
      .then((res) => {
        setProductOpts(
          (res.data || []).map((item) => ({
            slNo: item.slNo,
            itemCode: item.itemCode || "",
            itemName: item.itemName || "",
          }))
        );
      })
      .catch((err) =>
        console.error("Error fetching products:", err.message)
      );
  }, [dbCode, apiBaseUrl]);

  // ── Fetch other-registration options (Location / Category / Sub category / Mfr) ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`)
      .then((res) => {
        const all = res.data || [];
        const toOpt = (rec) => ({
          label: rec.Name || rec.name || "",
          value: Number(rec.Auto || rec.auto) || 0,
        });
        const locs = all
          .filter((r) => String(r.Type || r.type || "").toLowerCase() === "location")
          .map(toOpt);
        setLocationOpts([
          { label: "All Locations", value: 0 },
          ...locs
        ]);

        // Pre-select current location by default
        const currentLocName = (
          activeLocation?.name ||
          sessionStorage.getItem("loginLocation") ||
          localStorage.getItem("loginLocation") ||
          ""
        ).trim().toLowerCase();

        const currentLocId = Number(
          activeLocation?.id ||
          sessionStorage.getItem("loginLocationId") ||
          localStorage.getItem("loginLocationId") ||
          0
        );

        const matchedLoc = locs.find(
          (l) => (currentLocId && l.value === currentLocId) ||
                 (currentLocName && l.label.trim().toLowerCase() === currentLocName)
        );

        if (matchedLoc) {
          setLocation(matchedLoc.value);
        } else if (locs.length > 0) {
          setLocation(locs[0].value);
        }

        setCategoryOpts(
          all.filter((r) => String(r.Type || r.type || "").toLowerCase() === "category").map(toOpt)
        );
        setSubCategoryOpts(
          all.filter((r) => String(r.Type || r.type || "").toLowerCase() === "sub category").map(toOpt)
        );
        setMfrOpts(
          all.filter((r) => String(r.Type || r.type || "").toLowerCase() === "mfr").map(toOpt)
        );
      })
      .catch((err) =>
        console.error("Error fetching other-registration options:", err.message)
      );
  }, [dbCode, apiBaseUrl, activeLocation]);

  // ── Fetch suppliers ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/common/cashOrSupplier/${dbCode}`)
      .then((res) => {
        setSupplierOpts(
          (res.data || []).map((item) => ({
            label: item.LedName || "",
            value: item.Ledcode || "",
          }))
        );
      })
      .catch((err) =>
        console.error("Error fetching suppliers:", err.message)
      );
  }, [dbCode, apiBaseUrl]);

  // ── Fetch salesman ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/common/selectAllSalesMan/${dbCode}`)
      .then((res) => {
        setSalesmanOpts(
          (res.data || []).map((item) => ({
            label: item.Name || "",
            value: item.Auto || "",
          }))
        );
      })
      .catch((err) =>
        console.error("Error fetching salesman:", err.message)
      );
  }, [dbCode, apiBaseUrl]);

  // ── Fetch Purchase Forms from FormRegistration ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/common/selectAllformDatas/${dbCode}`)
      .then((res) => {
        const purchaseOnly = (res.data || [])
          .filter((item) => {
            const vType = String(item.fmrTypeOfVoucher || "").toLowerCase();
            const fName = String(item.fmrName || "").toLowerCase();
            return vType.includes("purchase") || fName.includes("purchase");
          })
          .map((item) => ({
            id: item.fmrEntryNo,
            label: item.fmrName,
            entryNo: item.fmrEntryNo,
            fmrName: item.fmrName,
            abbr: item.fmrAbbreviation,
            voucherType: item.fmrTypeOfVoucher,
          }));
        setPurchaseForms(purchaseOnly);
        setSelectedForms(purchaseOnly.map(f => f.label));
      })
      .catch((err) =>
        console.error("Error fetching purchase forms:", err.message)
      );
  }, [dbCode, apiBaseUrl]);

  // ── Fetch Tax Groups ──
  const [taxGroupOpts, setTaxGroupOpts] = useState([]);
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/taxgroup/fetchSavedData?dbCode=${dbCode}`)
      .then((res) => {
        setTaxGroupOpts(
          (res.data || []).map((item) => ({
            label: item.Name || "",
            value: item.Auto || item.Name || "",
          }))
        );
      })
      .catch((err) =>
        console.error("Error fetching tax groups:", err.message)
      );
  }, [dbCode, apiBaseUrl]);

  // ── Fetch Stock Groups ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/common/getStockGroups/${dbCode}`)
      .then((res) => {
        setGroupOpts(
          (res.data || []).map((item) => ({
            label: item.GroupName || item.groupName || "",
            value: item.Auto || item.id || 0,
          }))
        );
      })
      .catch((err) => console.error("Error fetching stock groups:", err.message));
  }, [dbCode, apiBaseUrl]);

  // ── Fetch Rate Class options ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/main/selectallrateClass/${dbCode}`)
      .then((res) => {
        setRateClassOpts(
          (res.data || []).map((item) => ({
            label: item.rateClass || "",
            value: item.id || 0,
          }))
        );
      })
      .catch((err) => console.error("Error fetching rate class options:", err.message));
  }, [dbCode, apiBaseUrl]);

  // ── Fetch Model options ──
  useEffect(() => {
    if (!dbCode) return;
    axios
      .get(`${apiBaseUrl}/model/selectall/${dbCode}`)
      .then((res) => {
        setModelOpts(
          (res.data || []).map((item) => ({
            label: item.ModelName || "",
            value: item.Auto || 0,
          }))
        );
      })
      .catch((err) => console.error("Error fetching model options:", err.message));
  }, [dbCode, apiBaseUrl]);

  // ── Derived combo options from products ──
  const itemCodeOpts = productOpts.map((p) => ({ label: p.itemCode, value: p.itemCode }));
  const itemNameOpts = productOpts.map((p) => ({ label: p.itemName, value: p.itemName }));

  const [voucherType, setVoucherType] = useState("All");
  const [periodType, setPeriodType] = useState("Period");
  const [selectedMonth, setSelectedMonth] = useState("");

  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayVal = getCurrentDate();

  const [fromDate, setFromDate] = useState(todayVal);
  const [toDate, setToDate] = useState(todayVal);

  const hasAlertedFyRef = useRef(false);
  useEffect(() => {
    if (activeFy && !hasAlertedFyRef.current) {
      if (!isDateInFy(todayVal)) {
        alert("Current date is outside the active financial year");
        hasAlertedFyRef.current = true;
      }
    }
  }, [activeFy, todayVal, isDateInFy]);

  const [orderBy, setOrderBy] = useState("");
  const [reportType, setReportType] = useState("Summary");
  const [itemCode, setItemCode] = useState("");
  const [itemName, setItemName] = useState("");
  const [supplier, setSupplier] = useState("");
  const [mfr, setMfr] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [location, setLocation] = useState(() => {
    return Number(
      sessionStorage.getItem("loginLocationId") ||
      localStorage.getItem("loginLocationId") ||
      0
    );
  });

  const [salesman, setSalesman] = useState("");
  const [project, setProject] = useState("");
  const [taxGroup, setTaxGroup] = useState("");
  const [taxType, setTaxType] = useState("");
  const [purchaseType, setPurchaseType] = useState("");
  const [group, setGroup] = useState("");
  const [rateClassFilter, setRateClassFilter] = useState("");
  const [modelNoFilter, setModelNoFilter] = useState("");

  const [selectedForms, setSelectedForms] = useState([]);

  const handleToggleForm = (formName) =>
    setSelectedForms((prev) =>
      prev.includes(formName) ? prev.filter((f) => f !== formName) : [...prev, formName]
    );

  const handleSelectAllForms   = () => setSelectedForms(purchaseForms.map(f => f.label));
  const handleUnSelectAllForms = () => setSelectedForms([]);

  const handleShowReport = () => {
    if (openReport) {
      const matchedProduct = productOpts.find(
        (p) => p.itemCode === itemCode || p.itemName === itemName
      );
      const itemId = matchedProduct ? matchedProduct.slNo : 0;

      const selectedFormIds = purchaseForms
        .filter((f) => selectedForms.includes(f.label))
        .map((f) => f.id);

      let finalFromDate = fromDate;
      let finalToDate = toDate;

      if (periodType === "Monthly" && selectedMonth) {
        const monthIndex = MONTHS.findIndex((m) => m.value === selectedMonth);
        if (monthIndex !== -1) {
          const year = new Date().getFullYear();
          const lastDay = new Date(year, monthIndex + 1, 0);
          const pad = (num) => String(num).padStart(2, "0");
          finalFromDate = `${year}-${pad(monthIndex + 1)}-01`;
          finalToDate = `${year}-${pad(monthIndex + 1)}-${pad(lastDay.getDate())}`;
        }
      }

      if (finalFromDate && !isDateInFy(finalFromDate)) {
        alert("From Date incompatible with financial year");
        return;
      }
      if (finalToDate && !isDateInFy(finalToDate)) {
        alert("To Date incompatible with financial year");
        return;
      }

      const activeLocId = (location !== undefined && location !== "" && location !== "All Locations") 
        ? Number(location) 
        : 0;

      openReport({ 
        voucherType, 
        periodType, 
        month: selectedMonth, 
        fromDate: finalFromDate, 
        toDate: finalToDate, 
        orderBy, 
        reportType, 
        itemCode, 
        itemName, 
        itemId, 
        supplier, 
        mfr, 
        category, 
        subCategory, 
        location: activeLocId,
        locationId: activeLocId, 
        salesman, 
        project, 
        taxGroup, 
        taxType, 
        purchaseType, 
        group,
        groupId:      group         ? Number(group)         : 0,
        rateClassId:  rateClassFilter ? Number(rateClassFilter) : 0,
        modelNo:      modelNoFilter  ? Number(modelNoFilter)  : 0,
        groupName:    group ? (groupOpts.find(g => Number(g.value) === Number(group))?.label || "") : "",
        rateClassName: rateClassFilter ? (rateClassOpts.find(r => Number(r.value) === Number(rateClassFilter))?.label || "") : "",
        modelName:    modelNoFilter ? (modelOpts.find(m => Number(m.value) === Number(modelNoFilter))?.label || "") : "",
        selectedForms,
        selectedFormIds,
        fyid: (fyid !== undefined && fyid !== null && fyid !== "") ? Number(fyid) : 0
      });
    }
  };

  const cbFont = { fontFamily: "Inter, sans-serif" };
  const labelCls = "text-[11px] text-black font-Inter flex-shrink-0";

  return (
    <div
      className="fixed inset-0 w-full h-full flex items-center justify-center bg-black bg-opacity-40"
      style={{ zIndex: zIndex || 100 }}
    >
      <div
        className="bg-white rounded-[10px] shadow-2xl flex flex-col overflow-hidden font-Inter"
        style={{ width: "820px", maxWidth: "98vw", maxHeight: "82vh" }}
      >
        {/* Header */}
        <WindowHeader
          title="Purchase List"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f56d3] h-[40px] text-white font-Inter"
          closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center"
        />

        {/* Body: Left + Right panels */}
        <div className="flex flex-1 overflow-hidden min-h-0">

          {/* ── LEFT PANEL ── */}
          <div className="flex flex-col border-r border-gray-300" style={{ width: "190px", minWidth: "190px" }}>
            <div className="px-3 py-[6px] text-[11px] font-semibold text-black font-Inter bg-[#eef1f8] border-b border-gray-300">
              Select Purchase Form
            </div>
            <div className="flex-1 overflow-y-auto bg-white">
              {purchaseForms.map((formObj) => (
                <label
                  key={formObj.id}
                  className={`flex items-center gap-2 px-2 py-[5px] cursor-pointer select-none text-[11px] font-Inter font-medium border-b border-gray-100 last:border-b-0 ${
                    selectedForms.includes(formObj.label)
                      ? "bg-[#dbeafe] text-[#1e40af]"
                      : "bg-white text-black hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedForms.includes(formObj.label)}
                    onChange={() => handleToggleForm(formObj.label)}
                    className="w-3.5 h-3.5 rounded border-gray-400 accent-[#1a56db] flex-shrink-0 cursor-pointer"
                  />
                  <span className="truncate">{formObj.label}</span>
                </label>
              ))}
            </div>
            {/* Select All / UnSelect All at bottom of left panel */}
            <div className="flex gap-1 p-2 border-t border-gray-200 bg-[#f9fafb]">
              <button type="button" onClick={handleSelectAllForms} className="flex-1 py-[3px] px-1 border border-gray-300 rounded-[3px] bg-white hover:bg-gray-100 text-[10px] text-black font-semibold cursor-pointer font-Inter">
                Select All
              </button>
              <button type="button" onClick={handleUnSelectAllForms} className="flex-1 py-[3px] px-1 border border-gray-300 rounded-[3px] bg-white hover:bg-gray-100 text-[10px] text-black font-semibold cursor-pointer font-Inter">
                UnSelect All
              </button>
            </div>
          </div>

          {/* ── RIGHT PANEL ── */}
          <div className="flex flex-col flex-1 overflow-hidden">

            {/* Radio Buttons */}
            <div className="flex items-center gap-5 px-4 py-1.5 border-b border-gray-200 bg-[#f8faff]">
              {["All", "Purchase", "Purchase Return", "Purchase Order", "UnR-Purchase"].map((type) => (
                <label key={type} className="flex items-center gap-1.5 cursor-pointer font-Inter text-black text-[11px]">
                  <input type="radio" name="voucherType" value={type} checked={voucherType === type} onChange={(e) => setVoucherType(e.target.value)} className="w-3.5 h-3.5 accent-[#0f56d3]" />
                  <span className="font-Inter">{type}</span>
                </label>
              ))}
            </div>

            {/* Filter Rows */}
            <div className="flex-1 overflow-y-auto px-4 py-1.5 flex flex-col gap-[4px] font-Inter">

              {/* Period Type + Month */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Period Type</span>
                <div className="flex-1">
                  <SherComboBox 
                    options={[{ label: "Period", value: "Period" }, { label: "Monthly", value: "Monthly" }]} 
                    value={periodType} 
                    onSelect={(opt) => setPeriodType(opt.value)} 
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
                <span className={`${labelCls} w-[45px] text-right`}>Month</span>
                <div className="flex-1">
                  <SherComboBox 
                    options={MONTHS} 
                    value={selectedMonth} 
                    onSelect={(opt) => setSelectedMonth(opt.value)} 
                  
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* From + To */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>From</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded-[3px] h-6 px-2 text-[11px] text-black font-Inter focus:outline-none focus:ring-1 focus:ring-[#0f56d3]"
                />
                <span className={`${labelCls} w-[45px] text-right`}>To</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                    }
                  }}
                  className="flex-1 border border-gray-300 rounded-[3px] h-6 px-2 text-[11px] text-black font-Inter focus:outline-none focus:ring-1 focus:ring-[#0f56d3]"
                />
              </div>

              {/* Order By */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Order By</span>
                <div className="flex-1">
                  <SherComboBox 
                    options={ORDER_BY_OPTIONS} 
                    value={orderBy} 
                    onSelect={(opt) => setOrderBy(opt.value)} 
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* Report Type */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Report Type</span>
                <div className="flex-1">
                  <SherComboBox 
                    options={REPORT_TYPES} 
                    value={reportType} 
                    onSelect={(opt) => setReportType(opt.value)} 
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* ItemCode — from product registration */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>ItemCode</span>
                <div className="flex-1">
                  <SherComboBox
                    options={itemCodeOpts}
                    value={itemCode}
                    onSelect={(opt) => {
                      setItemCode(opt.label);
                      const match = productOpts.find((p) => p.itemCode === opt.label);
                      if (match) setItemName(match.itemName);
                    }}
                    onInputChange={(text) => setItemCode(text)}
                    onFreeInput={(text) => setItemCode(text)}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

              {/* ItemName — from product registration */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>ItemName</span>
                <div className="flex-1">
                  <SherComboBox
                    options={itemNameOpts}
                    value={itemName}
                    onSelect={(opt) => {
                      setItemName(opt.label);
                      const match = productOpts.find((p) => p.itemName === opt.label);
                      if (match) setItemCode(match.itemCode);
                    }}
                    onInputChange={(text) => setItemName(text)}
                    onFreeInput={(text) => setItemName(text)}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

              {/* Supplier */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Supplier</span>
                <div className="flex-1">
                  <SherComboBox 
                    options={supplierOpts} 
                    value={supplier} 
                    onSelect={(opt) => setSupplier(opt ? opt.value : "")} 
                    onClear={() => setSupplier("")}
                    onInputChange={(text) => { if (!text) setSupplier(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* Mfr + Category — from other registration */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Mfr</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={mfrOpts} 
                    value={mfr} 
                    onSelect={(opt) => setMfr(opt ? opt.value : "")} 
                    onClear={() => setMfr("")}
                    onInputChange={(text) => { if (!text) setMfr(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
                <span className={`${labelCls} w-[60px] text-right`}>Category</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={categoryOpts} 
                    value={category} 
                    onSelect={(opt) => setCategory(opt ? opt.value : "")} 
                    onClear={() => setCategory("")}
                    onInputChange={(text) => { if (!text) setCategory(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* Sub Category + Location — from other registration */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Sub Category</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={subCategoryOpts} 
                    value={subCategory} 
                    onSelect={(opt) => setSubCategory(opt ? opt.value : "")} 
                    onClear={() => setSubCategory("")}
                    onInputChange={(text) => { if (!text) setSubCategory(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
                <span className={`${labelCls} w-[60px] text-right`}>Location</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={locationOpts} 
                    value={location} 
                    onSelect={(opt) => setLocation(opt ? opt.value : 0)} 
                    onClear={() => setLocation(0)}
                    onInputChange={(text) => { if (!text) setLocation(0); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={{ ...cbFont }} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* SalesMan + Project */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>SalesMan</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={salesmanOpts} 
                    value={salesman} 
                    onSelect={(opt) => setSalesman(opt ? opt.value : "")} 
                    onClear={() => setSalesman("")}
                    onInputChange={(text) => { if (!text) setSalesman(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
                <span className={`${labelCls} w-[60px] text-right`}>Project</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={[]} 
                    value={project} 
                    onSelect={(opt) => setProject(opt ? opt.value : "")} 
                    onClear={() => setProject("")}
                    onInputChange={(text) => { if (!text) setProject(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* Tax Group + Tax Type */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Tax Group</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={taxGroupOpts} 
                    value={taxGroup} 
                    onSelect={(opt) => setTaxGroup(opt ? opt.value : "")} 
                    onClear={() => setTaxGroup("")}
                    onInputChange={(text) => { if (!text) setTaxGroup(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
                <span className={`${labelCls} w-[60px] text-right`}>Tax Type</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox 
                    options={TAX_TYPES} 
                    value={taxType} 
                    onSelect={(opt) => setTaxType(opt ? opt.value : "")} 
                    onClear={() => setTaxType("")}
                    onInputChange={(text) => { if (!text) setTaxType(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* Purchase Type */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Purchase Type</span>
                <div className="flex-1">
                  <SherComboBox 
                    options={PURCHASE_TYPES} 
                    value={purchaseType} 
                    onSelect={(opt) => setPurchaseType(opt ? opt.value : "")} 
                    onClear={() => setPurchaseType("")}
                    onInputChange={(text) => { if (!text) setPurchaseType(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter" 
                    inputStyle={cbFont} 
                    inputHeight="24px" 
                  />
                </div>
              </div>

              {/* Group */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Group</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox
                    options={groupOpts}
                    value={group}
                    onSelect={(opt) => setGroup(opt ? opt.value : "")}
                    onClear={() => setGroup("")}
                    onInputChange={(text) => { if (!text) setGroup(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
                <span className={`${labelCls} w-[60px] text-right`}>Rate Class</span>
                <div style={{ flex: 1 }}>
                  <SherComboBox
                    options={rateClassOpts}
                    value={rateClassFilter}
                    onSelect={(opt) => setRateClassFilter(opt ? opt.value : "")}
                    onClear={() => setRateClassFilter("")}
                    onInputChange={(text) => { if (!text) setRateClassFilter(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

              {/* Model */}
              <div className="flex items-center gap-2">
                <span className={`${labelCls} w-[80px]`}>Model</span>
                <div className="flex-1">
                  <SherComboBox
                    options={modelOpts}
                    value={modelNoFilter}
                    onSelect={(opt) => setModelNoFilter(opt ? opt.value : "")}
                    onClear={() => setModelNoFilter("")}
                    onInputChange={(text) => { if (!text) setModelNoFilter(""); }}
                    inputClassName="w-full border border-gray-300 rounded-[3px] font-Inter"
                    inputStyle={cbFont}
                    inputHeight="24px"
                  />
                </div>
              </div>

            </div>

            {/* Footer Buttons */}
            <div className="flex justify-center items-center gap-4 border-t border-gray-200 py-2.5 bg-white">
              <button onClick={handleShowReport} className="bg-[#2563eb] text-white hover:bg-blue-700 transition flex items-center justify-center gap-2 py-1.5 px-6 rounded-[4px] shadow-sm font-semibold cursor-pointer text-xs font-Inter">
                <img src={checkMark} className="w-3.5 h-3.5 filter invert" alt="Show" />
                <span className="font-Inter">Show</span>
              </button>
              <button onClick={onClose} className="bg-gray-100 text-black hover:bg-gray-200 transition flex items-center justify-center gap-2 py-1.5 px-6 rounded-[4px] shadow-sm font-semibold border border-gray-300 cursor-pointer text-xs font-Inter">
                <img src={cross} className="w-3.5 h-3.5" alt="Exit" />
                <span className="font-Inter">Exit</span>
              </button>
            </div>

          </div>
          {/* ── END RIGHT PANEL ── */}

        </div>
      </div>
    </div>
  );
}

export default PurchaseListDialog;